/**
 * Phase F.6 Batch B — Inventory AI + Config + Waitlist UI.
 *
 * Three components exported from this file:
 *   - InventoryConfigCard:  per-tenant rules (SKU mandatory, negative-stock policy, defaults)
 *   - InventoryAIScanDialog: modal — upload image -> Nano Banana -> review -> bulk-create
 *   - InventoryWaitlistTab: list waitlist entries, cancel
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Upload, Sparkles, Loader2, X, Trash2, ShoppingBag, Save, Eye, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =============================================================================
// Inventory Config Card
// =============================================================================

export function InventoryConfigCard({ onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/inventory/config`).then(r => setCfg(r.data)).catch(() => setCfg(null));
  }, []);

  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/inventory/config`, {
        sku_mandatory: cfg.sku_mandatory,
        negative_stock_policy: cfg.negative_stock_policy,
        default_low_stock_threshold: Number(cfg.default_low_stock_threshold) || 0,
        ai_scan_enabled: cfg.ai_scan_enabled,
        employee_portal_visible_default: cfg.employee_portal_visible_default,
        client_portal_visible_default: cfg.client_portal_visible_default,
      });
      setCfg(data);
      toast.success("Inventory rules saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Loading config…</CardContent></Card>;
  if (cfg.no_tenant) {
    return (
      <Card data-testid="inv-config-no-tenant">
        <CardContent className="py-6 text-sm text-muted-foreground">
          Inventory rules are configured per tenant. Your platform admin account is not bound to a tenant — switch into a tenant context to manage these settings.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="inv-config-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Inventory Rules</CardTitle>
        <CardDescription>Per-tenant rules applied to every SKU and stock movement.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <Label className="text-sm font-medium">SKU required on new items</Label>
            <p className="text-xs text-muted-foreground mt-1">When ON, every new inventory item must have a non-empty SKU.</p>
          </div>
          <Switch checked={!!cfg.sku_mandatory} onCheckedChange={(v) => set("sku_mandatory", !!v)} data-testid="cfg-sku-mandatory" />
        </div>

        <div className="rounded-md border p-3 space-y-2">
          <Label className="text-sm font-medium">Negative-stock policy</Label>
          <p className="text-xs text-muted-foreground">What happens when a job or stock adjustment would push quantity below 0.</p>
          <Select value={cfg.negative_stock_policy || "soft_warn"} onValueChange={(v) => set("negative_stock_policy", v)}>
            <SelectTrigger data-testid="cfg-negative-policy"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="soft_warn">Soft warn (allow, flag as backorder needed)</SelectItem>
              <SelectItem value="hard_block">Hard block (refuse the change with 409)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-md border p-3">
            <Label className="text-sm font-medium">Default low-stock threshold</Label>
            <p className="text-xs text-muted-foreground mb-2">Applied to new items when not set explicitly.</p>
            <Input type="number" step="0.1" min="0"
              value={cfg.default_low_stock_threshold ?? 0}
              onChange={(e) => set("default_low_stock_threshold", e.target.value)}
              data-testid="cfg-low-thresh" />
          </div>

          <div className="rounded-md border p-3 flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">AI Scanner enabled</Label>
              <p className="text-xs text-muted-foreground mt-1">Allow staff to use the AI image scanner (Nano Banana).</p>
            </div>
            <Switch checked={!!cfg.ai_scan_enabled} onCheckedChange={(v) => set("ai_scan_enabled", !!v)} data-testid="cfg-ai-enabled" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-md border p-3 flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">New items visible to employees</Label>
              <p className="text-xs text-muted-foreground mt-1">Default for the "show in employee portal" toggle on new items.</p>
            </div>
            <Switch checked={!!cfg.employee_portal_visible_default} onCheckedChange={(v) => set("employee_portal_visible_default", !!v)} data-testid="cfg-emp-vis" />
          </div>
          <div className="rounded-md border p-3 flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">New items visible to clients</Label>
              <p className="text-xs text-muted-foreground mt-1">Default for the "show in client portal" toggle on new items.</p>
            </div>
            <Switch checked={!!cfg.client_portal_visible_default} onCheckedChange={(v) => set("client_portal_visible_default", !!v)} data-testid="cfg-cli-vis" />
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="cfg-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Rules
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// AI Scan Dialog
// =============================================================================

export function InventoryAIScanDialog({ open, onOpenChange, mode = "shipment", onItemsApplied }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [imageB64, setImageB64] = useState(null);
  const [hint, setHint] = useState("");
  const [scanning, setScanning] = useState(false);
  const [items, setItems] = useState([]);
  const [latency, setLatency] = useState(null);

  useEffect(() => {
    if (!open) {
      setPreview(null); setImageB64(null); setHint(""); setItems([]); setLatency(null);
    }
  }, [open]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Image must be 6MB or smaller"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setPreview(dataUrl);
      const b64 = String(dataUrl).split(",")[1] || "";
      setImageB64(b64);
    };
    reader.readAsDataURL(f);
  };

  const runScan = async () => {
    if (!imageB64) { toast.error("Please upload an image first"); return; }
    setScanning(true);
    setItems([]);
    try {
      const { data } = await axios.post(`${API}/inventory/ai-scan`, {
        mode,
        image_base64: imageB64,
        image_mime: "image/jpeg",
        hint: hint || null,
        candidates_limit: 10,
      }, { timeout: 60000 });
      setItems(data.items || []);
      setLatency(data.latency_ms);
      if (!data.items?.length) toast.info("AI found no items in this image — try another photo or add a hint.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI scan failed");
    } finally {
      setScanning(false);
    }
  };

  const applyToCatalog = async () => {
    if (!items.length) return;
    let created = 0, failed = 0;
    for (const it of items) {
      try {
        await axios.post(`${API}/inventory/items`, {
          sku: it.sku || "",
          name: it.name,
          item_type: "consumable",
          unit: it.unit || "ea",
          unit_cost: Number(it.unit_cost) || 0,
          unit_price: 0,
          quantity_on_hand: Number(it.quantity) || 0,
        });
        created += 1;
      } catch (e) {
        failed += 1;
      }
    }
    toast.success(`Applied ${created} item(s) to inventory${failed ? ` — ${failed} skipped` : ""}`);
    onItemsApplied?.(created);
    onOpenChange(false);
  };

  const removeRow = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));
  const updateRow = (i, k, v) => setItems(prev => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="ai-scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-cyan-500" />
            AI Scan {mode === "shipment" ? "— Packing Slip / Shipment" : "— SKU Match"}
          </DialogTitle>
          <DialogDescription>
            {mode === "shipment"
              ? "Snap a photo of the packing slip or shipment label. The AI will extract SKUs, names, quantities, and unit costs."
              : "Snap a photo of a physical item. The AI will return the top matches from your existing SKU catalog."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg border border-dashed p-4 flex items-center gap-3">
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="ai-upload-btn">
              <Upload className="h-4 w-4 mr-2" /> Choose Image
            </Button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="ai-file-input" />
            {preview && (
              <img src={preview} alt="preview" className="h-20 max-w-[200px] rounded border object-contain" />
            )}
            {preview && (
              <Button variant="ghost" size="sm" onClick={() => { setPreview(null); setImageB64(null); }}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div>
            <Label className="text-xs">Optional hint (e.g. "brake pads, 5-pack box")</Label>
            <Input value={hint} onChange={(e) => setHint(e.target.value)} placeholder="optional context to help the model" data-testid="ai-hint-input" />
          </div>

          <Button onClick={runScan} disabled={scanning || !imageB64} className="w-full" data-testid="ai-run-scan-btn">
            {scanning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing…</> : <><Sparkles className="h-4 w-4 mr-2" /> Run AI Scan</>}
          </Button>

          {latency !== null && (
            <p className="text-xs text-muted-foreground">Analyzed in {latency} ms — {items.length} item(s) found.</p>
          )}

          {!!items.length && mode === "shipment" && (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-24">Qty</TableHead>
                    <TableHead className="w-28">Unit cost</TableHead>
                    <TableHead className="w-16">Conf</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it, i) => (
                    <TableRow key={i} data-testid={`ai-result-row-${i}`}>
                      <TableCell><Input value={it.sku || ""} onChange={(e) => updateRow(i, "sku", e.target.value)} className="h-7 text-xs" /></TableCell>
                      <TableCell><Input value={it.name || ""} onChange={(e) => updateRow(i, "name", e.target.value)} className="h-7 text-xs" /></TableCell>
                      <TableCell><Input type="number" step="0.01" value={it.quantity || 1} onChange={(e) => updateRow(i, "quantity", e.target.value)} className="h-7 text-xs" /></TableCell>
                      <TableCell><Input type="number" step="0.01" value={it.unit_cost ?? 0} onChange={(e) => updateRow(i, "unit_cost", e.target.value)} className="h-7 text-xs" /></TableCell>
                      <TableCell><Badge variant="outline">{Math.round((it.confidence ?? 0) * 100)}%</Badge></TableCell>
                      <TableCell><Button variant="ghost" size="sm" onClick={() => removeRow(i)}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!!items.length && mode === "match" && (
            <div className="rounded-md border p-3 space-y-1.5">
              {items.map((m, i) => (
                <div key={i} className="flex items-center justify-between border-b last:border-0 py-1.5" data-testid={`ai-match-${i}`}>
                  <div>
                    <p className="text-sm font-medium">{m.name}</p>
                    <p className="text-xs text-muted-foreground">{m.sku ? `SKU: ${m.sku}` : "no SKU"} · {m.reason}</p>
                  </div>
                  <Badge variant="outline">{Math.round((m.confidence ?? 0) * 100)}%</Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {mode === "shipment" && (
            <Button onClick={applyToCatalog} disabled={!items.length} data-testid="ai-apply-btn">
              <ShoppingBag className="h-4 w-4 mr-2" />
              Apply {items.length} item{items.length !== 1 ? "s" : ""} to inventory
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Waitlist Tab
// =============================================================================

export function InventoryWaitlistTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/inventory/waitlist?limit=200`);
      setEntries(data.entries || []);
    } catch {
      toast.error("Failed to load waitlist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await axios.delete(`${API}/inventory/waitlist/${id}`);
      toast.success("Cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel");
    }
  };

  return (
    <Card data-testid="inv-waitlist-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Eye className="h-5 w-5" /> Restock Waitlist</CardTitle>
        <CardDescription>Anyone (staff or client) waiting on out-of-stock items. Notified automatically on restock.</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p> : (
          entries.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No waitlist entries.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Requester</TableHead>
                  <TableHead className="w-24">Qty</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(e => (
                  <TableRow key={e.id} data-testid={`waitlist-row-${e.id}`}>
                    <TableCell className="font-mono text-xs">{e.item_id?.slice(0, 8)}…</TableCell>
                    <TableCell>{e.requester_name || e.requester_email || "—"}</TableCell>
                    <TableCell>{e.quantity_requested}</TableCell>
                    <TableCell>
                      <Badge variant={e.status === "notified" ? "default" : e.status === "cancelled" ? "outline" : "secondary"}>
                        {e.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {e.created_at ? new Date(e.created_at).toLocaleString() : ""}
                    </TableCell>
                    <TableCell>
                      {e.status === "pending" && (
                        <Button variant="ghost" size="sm" onClick={() => cancel(e.id)} data-testid={`waitlist-cancel-${e.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        )}
      </CardContent>
    </Card>
  );
}
